import { useTranslation, Trans } from 'react-i18next';
import { t } from 'i18next';
import { Button, cn } from '@c2fo/liquidity';
import { PlusIcon, TechnicalDifficultyIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import NoDataState from '@/enterprise/components/NoDataState';
import BuyerLogoList from '@/enterprise/components/OfferBuilder/OfferReview/BuyerLogoList';
import Skeleton from '@/enterprise/components/Skeleton';
import useAggregateTakerMarketStats from '@/enterprise/data/useAggregateTakerMarketStats';
import useTakerMarkets, { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useMarketStatsStatus } from '@/lib/useMarketStatsStatus';
import { useIntercom } from '@/reporting/Intercom';
import IntercomDataTarget from '@/reporting/IntercomDataTarget';
import { ButtonLink } from '@/shared/components/ButtonLink';
import Divider from '@/shared/components/Divider';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import useExchangeRates from '@/shared/data/useExchangeRates';
import useSrm from '@/shared/data/useSrm';
import convertCurrency from '@/utils/convertCurrency';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import handleNaN from '@/utils/handleNaN';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { useReadOnlyUser } from '@/utils/useRestrictions';
import { isTakerMarketEligibleForOffer } from '../../offerModal/utils';
import AcceptanceCard, { ACCEPTANCE_CARD_TYPES } from './AcceptanceCard';
import MarketingWidget from './MarketingWidget/MarketingWidget';

// Content for when the user has zero eligible invoices
// Directs user to add customers
const NoEligibleInvoicesContent = () => {
  const { t } = useTranslation();
  return (
    <>
      <h2 className="m-0 font-serif text-2xl text-white lg:text-4xl">{t('core.nothingAvailable')}</h2>
      <p className="pt-1 text-white">{t('homePageBanner.addYourCustomers')}</p>
      <div className="mt-7 flex flex-col items-center rounded-2xl border bg-white px-1 py-7 text-center lg:mt-9 lg:py-9">
        <p className="pb-6">{t('homePageBanner.addCustomersDescription')}</p>
        <ButtonLink
          to={asEnterpriseLink('find-your-customers/expand-your-network')}
          variant="cta"
          icon={<PlusIcon className="fill-white" />}
        >
          {t('core.addCustomers')}
        </ButtonLink>
      </div>
      <div className="pt-7 text-center text-white lg:pt-9">
        <Trans i18nKey="core.haveQuestions" components={{ style: <span className="font-bold" /> }} />
      </div>
    </>
  );
};

const AcceptOfferContent = ({
  listeningForMarketStats,
  totalEligibleInvoiceAmountSelectedCurrency,
  takerMarkets,
}: {
  listeningForMarketStats: boolean;
  totalEligibleInvoiceAmountSelectedCurrency: string;
  takerMarkets: TakerMarket[];
}) => {
  const { t } = useTranslation();
  const { openChat } = useIntercom();
  const srm = useSrm();
  const { selectedCurrency } = useCurrencyContext();
  const { data: exchangeRates } = useExchangeRates();
  const { asNumber, asDateStringText } = useLocaleFormat();

  const stats = takerMarkets.reduce<{
    avgDaysPaidEarly: number;
    earliestPayDate: Date | null;
    earliestGoodFundsDate: Date | null;
    eligibleDpeWeightedSum: number;
    eligibleInvoiceAmount: number;
    eligibleInvoiceCount: number;
    latestPayDate: Date | null;
    latestGoodFundsDate: Date | null;
  }>(
    (acc, curr) => {
      acc.eligibleInvoiceCount += curr.eligibleInvoiceCount;

      const convertedDpeWeightedSum = convertCurrency({
        amount: curr.eligibleDpeWeightedSum,
        exchangeRates,
        from: curr.currency,
        to: selectedCurrency,
      });
      const convertedInvoiceAmount = convertCurrency({
        amount: curr.eligibleInvoiceAmount,
        exchangeRates,
        from: curr.currency,
        to: selectedCurrency,
      });

      acc.eligibleDpeWeightedSum += convertedDpeWeightedSum;
      acc.eligibleInvoiceAmount += convertedInvoiceAmount;

      // get earliest and latest marketPayDate
      if (curr.marketPayDate) {
        const currentDate = new Date(curr.marketPayDate);

        if (!acc.earliestPayDate || currentDate < acc.earliestPayDate) {
          acc.earliestPayDate = currentDate;
        }

        if (!acc.latestPayDate || currentDate > acc.latestPayDate) {
          acc.latestPayDate = currentDate;
        }
      }

      // get earliest and latest marketGoodFundsDate
      if (curr.marketGoodFundsDate) {
        const currentDate = new Date(curr.marketGoodFundsDate);

        if (!acc.earliestGoodFundsDate || currentDate < acc.earliestGoodFundsDate) {
          acc.earliestGoodFundsDate = currentDate;
        }

        if (!acc.latestGoodFundsDate || currentDate > acc.latestGoodFundsDate) {
          acc.latestGoodFundsDate = currentDate;
        }
      }

      return acc;
    },
    {
      avgDaysPaidEarly: 0,
      earliestPayDate: null,
      earliestGoodFundsDate: null,
      eligibleDpeWeightedSum: 0,
      eligibleInvoiceAmount: 0,
      eligibleInvoiceCount: 0,
      latestPayDate: null,
      latestGoodFundsDate: null,
    }
  );

  const formatDateRange = (earliest: Date | null, latest: Date | null) => {
    if (!earliest || !latest) {
      return '--';
    }

    const formatDateWithoutYear = (date: Date) => asDateStringText({ date, formatOptions: { year: undefined } });
    return formatDateWithoutYear(earliest) === formatDateWithoutYear(latest)
      ? formatDateWithoutYear(earliest)
      : `${formatDateWithoutYear(earliest)} - ${formatDateWithoutYear(latest)}`;
  };

  // format the min and max date ranges for the good funds date and pay date
  // if the min and max date ranges are the same, display the single date
  // if the min and max date ranges are different, display the range
  // if there are no dates, display --
  const goodFundsDateRange = formatDateRange(stats.earliestGoodFundsDate, stats.latestGoodFundsDate);

  // calculate average days paid early
  stats.avgDaysPaidEarly =
    stats.eligibleInvoiceAmount > 0 ? stats.eligibleDpeWeightedSum / stats.eligibleInvoiceAmount : 0;

  return (
    <>
      <h2 className={cn('m-0 font-serif text-2xl text-white lg:text-5xl', { 'blur-sm': listeningForMarketStats })}>
        {totalEligibleInvoiceAmountSelectedCurrency}
      </h2>
      <p className="text-white">{t('core.totalEligibleAmount')}</p>
      <div className="mt-1 flex flex-col rounded-2xl border bg-white p-4">
        <div className="flex flex-col gap-y-2 sm:flex-row sm:justify-between">
          {/* Buyer logo(s) and name(s) */}
          <div className="flex flex-row items-start">
            <div className="flex flex-col">
              <BuyerLogoList takerMarkets={takerMarkets} />
              <p className="text-xs font-normal text-text-secondary">{t('core.acceptFirstOffer')}</p>
            </div>
          </div>
          {/* Rate section */}
          {/* TODO: Add rate here once that is supported. Ticket: SUPP-16564 */}
        </div>
        {/* Stat cards */}
        {/* Total included eligible invoices, estimated pay date, average days paid early */}
        <div className="flex flex-col gap-2 px-2 py-5 sm:flex-row sm:justify-between sm:px-6">
          <div className="flex flex-1 flex-col items-center rounded-md bg-canvas p-4 text-center">
            <p className={cn('font-medium', { 'blur-sm': listeningForMarketStats })}>{stats.eligibleInvoiceCount}</p>
            <p className="text-xs font-medium capitalize text-text-secondary">
              {stats.eligibleInvoiceCount === 1 ? t('core.invoice') : t('core.invoicePlural')}
            </p>
          </div>
          <div className="flex flex-1 flex-col items-center rounded-md bg-canvas p-4 text-center">
            <p className={cn('font-medium', { 'blur-sm': listeningForMarketStats })}>{goodFundsDateRange}</p>
            <p className="text-xs font-medium text-text-secondary">{t('draftOffer.labels.estimatedDepositDate')}</p>
          </div>
          <div className="flex flex-1 flex-col items-center rounded-md bg-canvas p-4 text-center">
            <p className={cn('font-medium', { 'blur-sm': listeningForMarketStats })}>
              {asNumber({ value: handleNaN(stats.avgDaysPaidEarly), decimals: 0 })}
            </p>
            <p className="text-xs font-medium text-text-secondary">{t('core.averageDaysPaidEarly')}</p>
          </div>
        </div>
        <Divider />
        {/* learn more about early pay, contact support text, and build offer button */}
        {/* TODO: Update to Accept Offer button when pricing is implemented. Ticket: SUPP-16564 */}
        <div className="flex flex-col gap-4 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col">
            <p className="text-sm font-medium text-text-secondary">{t('core.learnMoreAboutEarlyPay')}</p>
            <p className="text-sm font-medium text-text-secondary">
              {/* If a valid srm email address is provided, show the chat with support or contact text */}
              {srm?.emailAddress ? (
                <Trans
                  i18nKey="core.chatWithSupportOrContact"
                  components={{
                    chatLink: (
                      <button type="button" className="font-medium text-blue-500 underline" onClick={openChat} />
                    ),
                    contactLink: (
                      <a
                        href={`mailto:${srm?.emailAddress}`}
                        className="font-medium text-blue-500 underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {/* NOTE: This text is a placeholder and will be swapped out with value in json file tag */}
                        contact
                      </a>
                    ),
                  }}
                />
              ) : (
                <Trans
                  i18nKey="core.chatWithSupport"
                  components={{
                    chatLink: (
                      <button type="button" className="font-medium text-blue-500 underline" onClick={openChat} />
                    ),
                  }}
                />
              )}
            </p>
          </div>
          <ButtonLink to={asEnterpriseLink('build-offer')} variant="cta" className="mt-2 w-full sm:mt-0 sm:w-fit">
            {t('core.buildOffer')}
          </ButtonLink>
        </div>
      </div>
    </>
  );
};

// TODO: remove enableAcceptOffer prop when ready to release AcceptOfferContent
const HomePageBannerContent = ({ enableAcceptOffer }: { enableAcceptOffer: boolean }) => {
  const { t } = useTranslation();
  const { selectedCurrency } = useCurrencyContext();
  const { asCurrency } = useLocaleFormat();
  const { data: takerMarkets } = useTakerMarkets();
  const takerMarketStats = useAggregateTakerMarketStats(takerMarkets);
  const { listenToAllMarketStats } = useMarketStatsStatus();
  const listeningForMarketStats = listenToAllMarketStats();
  const readOnlyUser = useReadOnlyUser();
  const zeroEligibleInvoices = takerMarketStats?.aggregateStats.eligibleInvoiceCount === 0;

  // If the user has zero eligible invoices, show content directing user to add customers
  if (zeroEligibleInvoices) {
    return <NoEligibleInvoicesContent />;
  }

  // return taker markets that are eligible for an offer
  // taker market is eligible for an offer if:
  // 1. it is not currently in an offer
  // 2. it has an eligible included invoice amount
  // 3. the market is enabled
  // 4. the taker market does not have a schedule
  const eligibleTakerMarkets = takerMarkets
    .filter(isTakerMarketEligibleForOffer)
    .filter((takerMarket) => takerMarket.eligibleInvoiceAmount > 0);

  const totalInvoiceAmountSelectedCurrency = asCurrency(
    takerMarketStats?.byCurrency[selectedCurrency].totalInvoiceAmount,
    selectedCurrency
  );

  // TODO: Once querying user offer history is supported add check for if user has ever had a cleared offer. Ticket: SUPP-16563
  if (!readOnlyUser && enableAcceptOffer && eligibleTakerMarkets.length > 0) {
    return (
      <AcceptOfferContent
        listeningForMarketStats={listeningForMarketStats}
        totalEligibleInvoiceAmountSelectedCurrency={totalInvoiceAmountSelectedCurrency}
        takerMarkets={eligibleTakerMarkets}
      />
    );
  }

  return (
    <>
      <div className="text-white">
        <h2 className="m-0 font-serif text-5xl">{totalInvoiceAmountSelectedCurrency}</h2>
        <p>{t('core.totalEligibleAmount')}</p>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-3 rounded-xl bg-white/10 p-4">
        {ACCEPTANCE_CARD_TYPES.map((cardType) => (
          <AcceptanceCard key={cardType} cardType={cardType} listeningForMarketStats={listeningForMarketStats} />
        ))}
      </div>
    </>
  );
};

const HomePageBannerSkeleton = () => (
  <div className="flex flex-col space-y-4">
    <Skeleton className="h-20 w-64" />
    <div className="grid grid-cols-1 gap-3">
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  </div>
);

const HomePageBannerError = () => (
  <NoDataState
    icon={<TechnicalDifficultyIcon className="h-24 w-24" />}
    message={t('core.errorLoadingMarketSummaryDescription')}
    action={
      <Button size="sm" variant="ancillary" inverse onClick={() => window.location.reload()}>
        {t('core.refresh')}
      </Button>
    }
    inverse
  />
);

const HomePageBanner = () => (
  <div className="flex flex-col gap-4 lg:flex-row">
    <IntercomDataTarget className="w-full lg:w-2/3" target="enterpriseMarketOverview">
      <div className="h-full rounded-2xl bg-secondary-500 p-6 shadow-md">
        <QueryBoundaries
          LoadingComponent={() => <HomePageBannerSkeleton />}
          ErrorComponent={() => <HomePageBannerError />}
        >
          <HomePageBannerContent enableAcceptOffer={false} />
        </QueryBoundaries>
      </div>
    </IntercomDataTarget>
    <div className="lg:flex-1">
      <MarketingWidget />
    </div>
  </div>
);

export default HomePageBanner;
